import _ from "lodash";
import React, { useCallback, useRef, useEffect, useState, useMemo } from "react";
import queryString from "query-string";
import moment from "moment";
import { useMedia } from "react-media";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { Form, Dropdown, Modal, Header, Button, Segment, Menu, Icon, Dimmer, Loader } from "semantic-ui-react";
import { useUserPreference } from "../../hooks/use-user-preference";
import { useStreamResourcePageEffects } from "../../hooks/use-stream-resource";
import { useStreamResourceViews } from "../../hooks/use-stream-resource-views";
import { useClient } from "../../hooks/use-client";
import { getCrmRequestImages } from "../../actions/crm_requests";
import { useEmployees } from "../../queries/employees";
import { useFeatureFlag } from "../../hooks/use-feature-flag";
import crmRequestsSelectors from "../../selectors/crm_requests";
import { useCrmRequests, CrmStreamTable } from "./hooks/hook";
import { OmniFilterPanel } from "../OmniFilter";
import { ControlledInput } from "../ControlledForm";
import { FilterContext } from "../filters/FilterContext";
import { DesktopColumns } from "./DesktopColumns";
import { MobileColumns } from "./MobileColumns";
import { IElasticsearchSort } from "../../utils/elasticsearch";
import AppSettings from "../../config";
import BulkEditModal from "../../components/crm/BulkEditModal";
import networkSelectors from "../../selectors/network";
import identitySelectors from "../../selectors/identity";

import "./List.scss";
import { getCrmRequestBulkEdit } from "../../queries/crmRequests";
import { useQuery } from "react-query";
import TopNavigation from "../global_components/TopNavigation";
import { RemoveViewModal } from "../../features/permits/components/RemoveViewModal";
import { PlusButton } from "../PlusButton";
import { useToasts } from "react-toast-notifications";
import Page403 from "../global_components/Page403";

const defaultSort = {
  field: "dateEntered",
  direction: "desc",
} as IElasticsearchSort;

export interface IRequestViews {
  id: string;
  attributes: {
    name: string;
    canOverride: boolean;
    filters: {};
    sort: IElasticsearchSort;
  };
}

export interface IChangeView {
  key: string;
  text: string;
  value: string;
}

const defaultViews: IRequestViews[] = [
  {
    id: "",
    attributes: {
      name: "All Requests",
      canOverride: false,
      filters: {},
      sort: defaultSort,
    },
  },
  {
    id: "-1",
    attributes: {
      name: "Assigned to Me",
      canOverride: false,
      filters: {
        assignedToId: ["me"],
        statusCategory: ["open"],
      },
      sort: defaultSort,
    },
  },
];

const CrmList = () => {
  const isSmallScreen = useMedia({ query: "(max-width: 1100px)" });
  const rowHeight = isSmallScreen ? 110 : 90;
  const isMobile = useMedia({ query: "(max-width: 567px)" });
  const headerRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const { site } = useParams<{ site: string }>();
  document.title = "Citizen Request Management";
  const { handleSubmit, control } = useForm({
    defaultValues: {
      name: "",
    },
  });
  const query = queryString.parse(window.location.search) as { message?: string };
  const { addToast } = useToasts();
  useEffect(() => {
    if (query.message) {
      addToast(query.message, { appearance: "info" });
    }
  }, []);

  const crmRequestImages = useSelector(crmRequestsSelectors.getCrmRequestImages);
  const [selectedCases, setSelectedCases] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState<boolean>(false);

  const listModeCrmPreference = useUserPreference("crm.list.mode");
  const listModeCePreference = useUserPreference("ce.list.mode");
  const landingPreference = useUserPreference("landing.home");

  const [selectedFilter, setSelectedFilter] = useState("-1");
  const [saveViewOpen, setSaveViewOpen] = useState(false);
  const [isViewDirty, setViewDirty] = useState(false);
  const [deleteViewConfirmationModal, setDeleteViewConfirmationModal] = useState({ open: false });
  const [editModalOpen, setEditModalOpen] = useState(false);
  const networkHeaders = useSelector(networkSelectors.defaultHeaders);
  const myBulkJobsQuery = useQuery(["MyBulkCrmJobs"], () =>
    getCrmRequestBulkEdit(networkHeaders)({ creatorIds: ["me"], statuses: ["pending", "running"] }),
  );
  const featureFlagBulkEdit = useFeatureFlag("crm.bulk_edit");
  const featureNewLookForce = useFeatureFlag("crm.new_look_force");
  const isBulkEditEnabled = featureFlagBulkEdit.isEnabled;

  const isCrmUser = useSelector(identitySelectors.isCrmUser);
  const isCrmAdmin = useSelector(identitySelectors.isCrmAdmin);
  const isDepartmentAdmin = useSelector(identitySelectors.isDepartmentAdmin);
  const isSuperAdmin = useSelector(identitySelectors.isSuperAdmin);

  const { clientId } = useClient(site);

  const EmployeesQuery = useEmployees({ includeDeactivated: true, fields: ["first-name", "last-name"] });
  const CrmRequests = useCrmRequests("crm.list", site);
  const {
    views,
    apply: applyView,
    save: saveView,
    delete: deleteView,
    isLoading,
  } = useStreamResourceViews(CrmRequests, defaultSort, defaultViews, "crm.list.filters");

  const selectedView = useMemo(() => {
    return views.find((view: IRequestViews) => view.id === selectedFilter);
  }, [selectedFilter, isLoading]);

  const [viewsLoadedOnce, setViewsLoadedOnce] = useState(false);
  //On the initial load of the views, we need to make sure the selected view is set correctly
  useEffect(() => {
    const waitingForViewLoads = !viewsLoadedOnce;
    if (waitingForViewLoads && views && views.length > 1 && isLoading == false) {
      setViewsLoadedOnce(true);
      const query = queryString.parse(location.search, { arrayFormat: "bracket", parseBooleans: false });
      let maxScore = 0;

      views.forEach((view: IRequestViews) => {
        const filters = _.get(view, "attributes.filters", {});
        const filterKeys = Object.keys(filters);

        let score = 0;

        // Since we don't track the view id in the query string, we need to find the view that matches the most filters
        for (const key of filterKeys) {
          if (_.isEqual(query[key], filters[key])) {
            score++;
            continue;
          }
          score--;
        }

        if (score > maxScore) {
          maxScore = score;
          setSelectedFilter(_.get(view, "id"));
        }
      });
    }
  }, [views]);

  const filterViewOptions = useMemo(() => {
    const dynamicOptions = views.map((view: IRequestViews) => {
      return {
        key: view.id,
        value: view.id,
        text: view.attributes.name,
      };
    });
    return dynamicOptions;
  }, [views]);

  const momentStart = useMemo(() => moment(), []);
  const canReturnToClassic = !featureNewLookForce.isEnabled && (site.startsWith("test") || clientId < 230);
  const imagesQueryIds = useMemo(
    () => CrmRequests.streamQuery.hitsWindow.map((item: any) => item._id) as [],
    [CrmRequests.streamQuery.hitsWindow],
  );

  const updateSelected = useCallback(
    (caseId: number) => {
      if (selectedCases.includes(caseId)) {
        setSelectedCases(_.remove(selectedCases, (selectedCaseId: number) => selectedCaseId !== caseId));
      } else {
        setSelectedCases([...selectedCases, caseId]);
      }
    },
    [selectedCases],
  );

  /**
   * When the user clicks on the select all checkbox, set state and select all the available cases.
   * Note that this does not select all the cases in the database, only the cases that are currently loaded in the table.
   *
   * @param checked
   */
  function onSelectAll(checked: boolean) {
    setSelectAll(checked);
    if (checked) {
      const allIds = CrmRequests.rows.map((hit: any) => hit.caseId);
      setSelectedCases(allIds);
    } else {
      setSelectedCases([]);
    }
  }

  const selectedCaseESIds = useMemo(() => {
    return CrmRequests.streamQuery.hitsWindow
      .map((item: any) => {
        return {
          esId: item._id,
          caseId: item._source.caseId,
        };
      })
      .filter((item: { esId: string; caseId: number }) => {
        return selectedCases.includes(item.caseId);
      })
      .map((item: { esId: string; caseId: number }) => item.esId);
  }, [CrmRequests.streamQuery.hitsWindow, selectedCases]);
  const sendToMapUrl = `/${site}/crm/map?${queryString.stringify(
    Object.assign({}, queryString.parse(window.location.search), { id: selectedCaseESIds }),
    { arrayFormat: "bracket" },
  )}`;
  const selectedCount = selectedCases.length;
  const canSendToMap = selectedCount > 0 && selectedCount < 100;
  const bulkDisableMessage = useMemo(() => {
    const currentJobStatus = _.get(myBulkJobsQuery.data, ["data", "0", "attributes", "status"], "");
    let message = "";
    if (currentJobStatus) {
      message = `(job ${currentJobStatus})`;
    } else if (selectedCount <= 0 && !selectAll) {
      message = "(select cases to start)";
    }
    return message;
  }, [myBulkJobsQuery, selectedCount, selectAll]);

  const columns = useMemo(() => {
    if (isSmallScreen) {
      return MobileColumns(crmRequestImages);
    }
    return DesktopColumns(site, crmRequestImages, selectAll, onSelectAll, selectedCases, updateSelected);
  }, [
    isSmallScreen,
    rowHeight,
    site,
    momentStart,
    EmployeesQuery.data,
    crmRequestImages,
    selectAll,
    setSelectAll,
    selectedCases,
    updateSelected,
  ]);

  const initialState = useMemo(
    () =>
      isSmallScreen ? {} : { sortBy: [{ id: CrmRequests.sort.field, desc: CrmRequests.sort.direction === "desc" }] },
    [isSmallScreen, CrmRequests.sort],
  );

  const headerBoundingRect = headerRef.current?.getBoundingClientRect();
  const headerHeight = (headerBoundingRect ? headerBoundingRect.y + headerBoundingRect.height : 0) + 60;
  const navBoundingRect = navRef.current?.getBoundingClientRect();
  const navHeight = (navBoundingRect ? navBoundingRect.y + navBoundingRect.height : 0) + 60;

  const offset = isMobile ? 45 : 40;
  const containerProps = {
    style: {
      display: "block",
      minHeight: `calc(100vh - ${headerHeight + navHeight - offset}px)`,
    },
  };
  const tableProps = useMemo(
    () => ({
      noRowsRenderer: () => (
        <div style={{ padding: "16px", display: "block", textAlign: "center" }}>
          <Header>No results</Header>
        </div>
      ),
      onSortChanged: (field: string, descending: boolean) => {
        if (!["dateEntered", "dateLastUpdated", "dateExpectClose"].includes(field)) {
          return;
        }
        setViewDirty(true);
        CrmRequests.streamQuery.scrollTop();
        CrmRequests.setSort({ field, direction: descending ? "desc" : "asc" });
      },
      headerHeight: 30,
      rowHeight: rowHeight,
      onRowClick: (data: any) => {
        const id = data.rowData.original.caseId;
        const pageWhenDone = encodeURIComponent(encodeURIComponent(window.location.href));
        const legacyUrl = `${AppSettings.GOGOV_WEB_URL}/${site}/editcase.php?cmd=edit&pagewhendone=${pageWhenDone}&id=${id}`;
        const legacyMobileUrl = `${AppSettings.GOGOV_MOBILE_URL}/${site}/editcase.php?cmd=edit&pagewhendone=${pageWhenDone}&id=${id}`;
        window.location.href = isSmallScreen ? legacyMobileUrl : legacyUrl;
      },
    }),
    [CrmRequests.streamQuery.scrollTop, CrmRequests.setSort, setViewDirty],
  );

  const pageWhenDone = encodeURIComponent(encodeURIComponent(window.location.href));

  const legacyUrl = `${AppSettings.GOGOV_WEB_URL}/${site}/editcase.php?cmd=new&pagewhendone=${pageWhenDone}`;
  const legacyMobileUrl = `${AppSettings.GOGOV_MOBILE_URL}/${site}/editcase.php?cmd=new&pagewhendone=${pageWhenDone}`;

  const referenceURL = isSmallScreen ? legacyMobileUrl : legacyUrl;

  useStreamResourcePageEffects(
    CrmRequests,
    selectedView,
    () => {
      setSelectedFilter("");
      setViewDirty(true);
    },
    `/${site}/crm/list?clearfilters=true&direction=desc&field=dateEntered`,
  );

  const handleViewSubmit = async (data: { name: string }) => {
    setSaveViewOpen(false);
    const uuid = await saveView(data);
    setSelectedFilter(uuid);
    setViewDirty(false);
  };

  // Load request images
  useEffect(() => {
    const filteredImagesQueryIds = _.without(imagesQueryIds, ..._.keys(crmRequestImages)).map((id) => id.split("_")[0]);
    if (_.isEmpty(filteredImagesQueryIds)) {
      return;
    }
    dispatch(getCrmRequestImages({ ids: filteredImagesQueryIds, limit: filteredImagesQueryIds.length }));
  }, [imagesQueryIds]);

  useEffect(() => {
    const query = queryString.parse(location.search, { arrayFormat: "bracket", parseBooleans: true });
    if (query.clearfilters) {
      setSelectedFilter("");
    }
  }, []);

  const trigger = (
    <div className='tw-inline-flex tw-items-center tw-gap-2'>
      <span className='tw-px-[6px] tw-py-[16px]'>
        <Icon name='options' /> View Options
      </span>
    </div>
  );

  const viewListTrigger = (
    <span className='tw-text-[24px] tw-font-bold'>
      {selectedView.attributes.name}
      <Icon name='dropdown' />
    </span>
  );

  const handleDeleteView = () => {
    setDeleteViewConfirmationModal({ open: true });
  };

  const handleReset = () => {
    setSelectedFilter("");
    applyView("");
    setViewDirty(false);
  };

  return (
    <>
      <div ref={navRef}>
        <TopNavigation
          tabs={[
            { to: "/crm/list", label: "Requests", activeWhenURLIncludes: "/crm/list" },
            { to: "/crm/reports", label: "Reports", activeWhenURLIncludes: "/crm/reports" },
          ]}
        />
      </div>
      <RemoveViewModal
        open={deleteViewConfirmationModal.open}
        close={() => setDeleteViewConfirmationModal({ open: false })}
        onSubmit={() => {
          deleteView(selectedFilter);
          setDeleteViewConfirmationModal({ open: false });
        }}
      />
      <Modal
        className='tw-w-[25%]'
        onClose={() => setSaveViewOpen(false)}
        onOpen={() => setSaveViewOpen(true)}
        open={saveViewOpen}
      >
        <Modal.Header>Save View</Modal.Header>
        <Modal.Content>
          <Form onSubmit={handleSubmit(handleViewSubmit)} autoComplete='off'>
            <Form.Field required>
              <label>Name</label>
              <ControlledInput control={control} name='name' />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button secondary onClick={() => setSaveViewOpen(false)}>
            Cancel
          </Button>
          <Button primary type='submit' onClick={handleSubmit(handleViewSubmit)}>
            Save
          </Button>
        </Modal.Actions>
      </Modal>

      {!isCrmUser && !isCrmAdmin && !isDepartmentAdmin && !isSuperAdmin ? (
        <Page403 />
      ) : (
        <>
          <Segment className='tw-mx-[8px] tw-my-[8px]' ref={headerRef}>
            <div
              className='tw-flex tw-flex-col tw-items-start tw-justify-between sm:tw-flex-row sm:tw-items-center'
              ref={headerRef}
            >
              <Dropdown trigger={viewListTrigger} className='viewOptions indented-items' scrolling>
                <Dropdown.Menu>
                  {filterViewOptions.map((view: IChangeView) => {
                    return (
                      <Dropdown.Item
                        key={view.key}
                        onClick={() => {
                          setSelectedFilter(view.key);
                          applyView(view.key);
                          setViewDirty(false);
                        }}
                      >
                        {view.text}
                      </Dropdown.Item>
                    );
                  })}
                </Dropdown.Menu>
              </Dropdown>
              <div className='tw-ml-auto tw-flex tw-space-x-0 sm:tw-space-x-1'>
                {canReturnToClassic && (
                  <Button
                    className='tw-bg-transparent tw-text-blue'
                    onClick={async () => {
                      await listModeCrmPreference.update("legacy");
                      await listModeCePreference.update("legacy");
                      window.location.href = `${AppSettings.GOGOV_WEB_URL}/${site}/internal.php`;
                    }}
                  >
                    Return to Classic
                  </Button>
                )}
                {landingPreference.value != "crm.list" && (
                  <Button
                    className='tw-bg-transparent tw-text-blue'
                    onClick={() => landingPreference.update("crm.list")}
                  >
                    Set as Default Page
                  </Button>
                )}
                <Button as='a' href={referenceURL} secondary className='tw-flex tw-items-center tw-justify-center'>
                  Create
                </Button>
              </div>
            </div>
            <div className='tw-mt-3 tw-flex tw-items-center '>
              <FilterContext.Provider
                value={{ analytics: { changed: "EVENT_CRM_FILTER_CHANGED" }, ...CrmRequests.omnifilters }}
              >
                <div className='tw-min-w-[200px] tw-flex-1 tw-items-center'>
                  <OmniFilterPanel
                    {...CrmRequests.omnifilters.props}
                    padding={"3px 0 0 6px"}
                    isSmallScreen={isSmallScreen}
                    analytics={{ changed: "EVENT_CRM_OMNIFILTER_CHANGED" }}
                  />
                </div>
              </FilterContext.Provider>
              <div className='tw-ml-auto tw-flex'>
                <Dropdown trigger={trigger} className='viewOptions'>
                  <Dropdown.Menu>
                    <Dropdown.Item text='Delete' onClick={handleDeleteView} />
                    <Dropdown.Item
                      text='Save As New...'
                      onClick={() => {
                        setSaveViewOpen(true);
                      }}
                      disabled={!isViewDirty}
                    />
                  </Dropdown.Menu>
                </Dropdown>
                {isViewDirty && (
                  <span className='max-sm:tw-p-0 tw-p-[6px]'>
                    <PlusButton onClick={handleReset}>
                      <Icon name='redo' />
                      Reset View
                    </PlusButton>
                  </span>
                )}
              </div>
            </div>
          </Segment>
          <div className='CrmListBody' style={{ paddingLeft: "8px", paddingRight: "6px", paddingBottom: 0 }}>
            <BulkEditModal
              selectedCases={selectedCases}
              setOpen={setEditModalOpen}
              open={editModalOpen}
              onComplete={CrmRequests.streamQuery.scrollTop}
              maxUpdateableCases={100}
            />
            <Menu attached='top' borderless>
              <Dropdown basic compact item text={`Bulk Actions (${selectedCount})`}>
                <Dropdown.Menu>
                  <Dropdown.Item as='a' target='_blank' href={sendToMapUrl} disabled={!canSendToMap}>
                    <Icon name='map' />
                    Send to map {!canSendToMap && <>(Select up to 100 pins)</>}
                  </Dropdown.Item>
                  {isBulkEditEnabled && (
                    <Dropdown.Item as='a' onClick={() => setEditModalOpen(true)} disabled={bulkDisableMessage != ""}>
                      <Icon name='edit outline' />
                      Edit {bulkDisableMessage}
                    </Dropdown.Item>
                  )}
                </Dropdown.Menu>
              </Dropdown>
              {!isSmallScreen && (
                <Menu.Item>
                  Showing {CrmRequests.rows.length} requests out of{" "}
                  {CrmRequests.streamQuery.totalHits?.relation === "gte" ? "over " : ""}
                  {CrmRequests.streamQuery.totalHits?.value} in {CrmRequests.streamQuery.lastSearchTimeMs / 1000.0}{" "}
                  seconds
                </Menu.Item>
              )}
              <Menu.Menu position='right'>
                <Menu.Item as={Button} onClick={CrmRequests.streamQuery.scrollTop}>
                  <Icon name='angle double up' />
                  Top
                </Menu.Item>
              </Menu.Menu>
            </Menu>
            <Segment attached='bottom' basic style={{ padding: 0 }}>
              <Dimmer inverted active={CrmRequests.streamQuery.isLoading}>
                <Loader />
              </Dimmer>
              <CrmStreamTable
                {...CrmRequests.streamQuery}
                sortBy={CrmRequests.sort.field}
                sortDirection={CrmRequests.sort.direction}
                rows={CrmRequests.rows}
                columns={columns}
                initialState={initialState}
                containerProps={containerProps}
                tableProps={tableProps}
              />
            </Segment>
          </div>
        </>
      )}
    </>
  );
};

export default CrmList;
